import React, { useMemo, useRef, useState, useCallback } from "react";
import Button from "./Button";

const bytesToSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const buildAcceptAttr = (acceptedFormats) => {
  if (!acceptedFormats || acceptedFormats.length === 0) return undefined;
  // Hỗ trợ đuôi (.pdf) và MIME (application/pdf)
  return acceptedFormats
    .map((f) => (f.includes("/") ? f : `.${f.replace(/^\./, "")}`))
    .join(",");
};

const DragDrop = ({
  label,
  onFileChange,
  acceptedFormats,
  maxFiles = 1,
  maxSizeMB = 25,
  disabled = false,
  onError, // optional: callback(errorMessage)
}) => {
  const [files, setFiles] = useState([]); // File[]
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const acceptAttr = useMemo(
    () => buildAcceptAttr(acceptedFormats),
    [acceptedFormats],
  );

  const emitError = useCallback(
    (msg) => {
      setError(msg);
      onError?.(msg);
    },
    [onError],
  );

  const clearError = useCallback(() => setError(""), []);

  const validateFile = useCallback(
    (file) => {
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        return `Kích thước file "${file.name}" vượt quá ${maxSizeMB}MB.`;
      }

      if (acceptedFormats && acceptedFormats.length > 0) {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        const mime = file.type?.toLowerCase() ?? "";

        const okByExt = acceptedFormats.some((f) => {
          if (f.includes("/")) return false; // MIME only
          const clean = f.replace(/^\./, "").toLowerCase();
          return ext === clean; // ext match
        });

        const okByMime = acceptedFormats.some((f) => {
          if (!f.includes("/")) return false; // ext only
          return mime === f.toLowerCase(); // mime match
        });

        if (!okByExt && !okByMime) {
          const exts = acceptedFormats
            .map((f) => (f.includes("/") ? f : `.${f.replace(/^\./, "")}`))
            .join(", ");
          return `Chỉ chấp nhận các định dạng: ${exts}`;
        }
      }

      return null;
    },
    [acceptedFormats, maxSizeMB],
  );

  const dedupe = useCallback((arr) => {
    const seen = new Set();
    const out = [];
    for (const f of arr) {
      const key = `${f.name}-${f.size}-${f.lastModified}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(f);
      }
    }
    return out;
  }, []);

  const applyFiles = useCallback(
    (incoming) => {
      if (!incoming || incoming.length === 0) return;

      clearError();

      // validate từng file, gom lỗi đầu tiên (ưu tiên rõ ràng cho user)
      for (const f of incoming) {
        const err = validateFile(f);
        if (err) {
          emitError(err);
          return;
        }
      }

      // gộp & dedupe
      let merged = dedupe([...files, ...incoming]);

      // cắt theo maxFiles
      if (merged.length > maxFiles) {
        emitError(`Chỉ được tải lên tối đa ${maxFiles} tệp.`);
        merged = merged.slice(0, maxFiles);
      }

      setFiles(merged);
      onFileChange?.(merged);
    },
    [
      files,
      maxFiles,
      onFileChange,
      validateFile,
      emitError,
      clearError,
      dedupe,
    ],
  );

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Chỉ tắt nếu rời khỏi chính khung
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    applyFiles(droppedFiles);
    setDragging(false);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    applyFiles(selectedFiles);
    // cho phép chọn lại cùng file sau đó
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    onFileChange?.(updated);
  };

  const openPicker = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const canAddMore = files.length < maxFiles;

  return (
    <div className="w-full">
      {/* Danh sách file đã chọn */}
      {files.length > 0 && (
        <ul className="mt-2 mb-3 text-sm space-y-3">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center justify-between w-full bg-gray-100 p-2 py-3 rounded-md"
            >
              <div className="flex gap-3 items-center min-w-0">
                <img
                  src="/images/document.png"
                  alt=""
                  className="size-[20px] shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-sm text-gray-700 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {bytesToSize(file.size)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                aria-label={`Xóa tệp ${file.name}`}
                className="p-1 hover:opacity-80"
              >
                <img
                  src="/icons/x-close.svg"
                  alt="Remove"
                  className="h-4 w-4"
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Khung drag & drop */}
      {canAddMore && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Kéo thả hoặc chọn tệp"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openPicker();
          }}
          onClick={openPicker}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={[
            "border-2 w-full border-dashed rounded-md p-4 text-center cursor-pointer",
            "flex flex-col justify-center items-center space-y-3 transition-colors",
            disabled
              ? "border-gray-200 bg-gray-50 cursor-default opacity-70"
              : dragging
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-blue-300",
          ].join(" ")}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            autoComplete="off"
            accept={acceptAttr}
            multiple={maxFiles > 1}
            disabled={disabled}
            onChange={handleFileSelect}
          />

          <img
            src="/images/drag-drop.png"
            alt=""
            className="w-[66px] h-[66px]"
          />

          <Button
            variant="primary"
            type="button"
            onClick={openPicker}
            disabled={disabled}
          >
            Tải lên
          </Button>

          <p className="block text-sm text-gray-500">
            {files.length > 0 ? `${files.length} tệp đã chọn` : label}
          </p>

          {acceptedFormats?.length > 0 && (
            <p className="text-xs text-gray-400">
              Định dạng chấp nhận:{" "}
              {acceptedFormats
                .map((f) => (f.includes("/") ? f : `.${f.replace(/^\./, "")}`))
                .join(", ")}
              {" · "}Tối đa {maxFiles} tệp · {maxSizeMB}MB/tệp
            </p>
          )}

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default DragDrop;
